import { API_CONFIG } from '../config/api.config';
import { storageService } from '../utils/storage';

async function adminFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = storageService.getToken();
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

// ===== SYSTEM USERS =====
export const adminUsersService = {
  async getAll(params?: { pageSize?: number; page?: number; sort?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    
    const endpoint = `/admin/users/?${queryParams.toString()}`;
    const response = await adminFetch<any>(endpoint);
    
    const users = (response.data.results || []).map((user: any) => ({
      ...user,
      id: user.id?.toString() || user.documentId,
      firstName: user.firstname,
      lastName: user.lastname,
      roleId: user.roles?.[0]?.id?.toString() || '',
      roleName: user.roles?.[0]?.name || '',
      isActive: user.isActive !== false && !user.blocked,
      lastLogin: user.lastLogin || undefined,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt
    }));
    
    return {
      data: users,
      pagination: response.data.pagination
    };
  },

  async getById(id: string | number) {
    const response = await adminFetch<any>(`/admin/users/${id}`);
    return response.data;
  },

  async create(userData: any) {
    const firstname = userData.firstName || userData.firstname || '';
    const lastname = userData.lastName || userData.lastname || '';

    // Strapi's admin API creates users as pending invites (with a
    // registrationToken); the password can only be set by completing the
    // registration, which also activates the account immediately.
    const response = await adminFetch<any>('/admin/users', {
      method: 'POST',
      body: JSON.stringify({
        email: userData.email,
        firstname,
        lastname,
        roles: userData.roleId ? [Number(userData.roleId)] : [],
      }),
    });
    const created = response.data;

    const registrationToken = created?.registrationToken;
    if (registrationToken && userData.password) {
      await adminFetch<any>('/admin/register', {
        method: 'POST',
        body: JSON.stringify({
          registrationToken,
          userInfo: { firstname, lastname, password: userData.password },
        }),
      });
    } else if (created?.id && userData.password) {
      // No registration token returned: set password + activate directly
      await adminFetch<any>(`/admin/users/${created.id}`, {
        method: 'PUT',
        body: JSON.stringify({ password: userData.password, isActive: true }),
      });
    }

    return created;
  },

  async update(id: string | number, userData: any) {
    const firstname = userData.firstName || userData.firstname || '';
    const lastname = userData.lastName || userData.lastname || '';

    const payload: Record<string, any> = {
      email: userData.email,
      firstname,
      lastname,
      roles: userData.roleId ? [Number(userData.roleId)] : undefined,
    };
    if (userData.password) {
      payload.password = userData.password;
      // Setting a password on a pending-invite user must also activate it,
      // otherwise the account stays unusable
      payload.isActive = true;
    }
    if (userData.isActive !== undefined) {
      payload.isActive = !!userData.isActive;
    }

    const response = await adminFetch<any>(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return response.data;
  },

  async delete(id: string | number) {
    const response = await adminFetch<any>(`/admin/users/${id}`, {
      method: 'DELETE',
    });
    return response.data;
  }
};

// ===== ROLES =====

export interface StrapiPermission {
  id?: number;
  action: string;
  actionParameters?: Record<string, any>;
  subject: string | null;
  properties?: { fields?: string[] };
  conditions?: any[];
}

export const adminRolesService = {
  /**
   * Fetch all available permissions from the /admin/permissions catalog (the
   * source the native admin UI uses; role 1 has no stored permission rows).
   */
  async getAllAvailablePermissions(): Promise<StrapiPermission[]> {
    try {
      const { catalogToPermissions } = await import('./admin-permission-catalog');
      const resp = await adminFetch<any>('/admin/permissions');
      const fromCatalog = catalogToPermissions(resp?.data?.sections);
      if (fromCatalog.length > 0) return fromCatalog;
    } catch { /* fall through to legacy source */ }
    try {
      const resp = await adminFetch<any>('/admin/roles/1/permissions');
      const perms = resp.data || resp || [];
      return Array.isArray(perms) ? perms : [];
    } catch {
      return [];
    }
  },

  /**
   * List all roles. Only fetches role detail per role (for code/usersCount).
   * Does NOT fetch permissions here to avoid N+1 overhead.
   */
  async getAll() {
    const response = await adminFetch<any>('/admin/roles/?');
    const rolesRaw: any[] = response.data || [];

    const roles = await Promise.all(
      rolesRaw.map(async (role: any) => {
        let detail: any = role;
        try {
          const detailResp = await adminFetch<any>(`/admin/roles/${role.id}`);
          detail = detailResp.data || detailResp || role;
        } catch { /* use list data */ }

        return {
          id: (detail.id || role.id)?.toString(),
          documentId: detail.documentId || '',
          name: detail.name || role.name || '',
          code: detail.code || role.code || '',
          description: detail.description || role.description || '',
          isSystem: (detail.code || role.code || '').startsWith('strapi-'),
          isActive: true,
          permissions: [] as any[],
          permissionCount: 0,
          usersCount: detail.usersCount ?? role.usersCount ?? role.nb_users ?? 0,
          createdAt: detail.createdAt || role.createdAt || '',
          updatedAt: detail.updatedAt || role.updatedAt || '',
          publishedAt: detail.publishedAt || '',
        };
      })
    );

    return { data: roles };
  },

  /**
   * Fetch raw permission objects for a role (includes properties.fields).
   */
  async getRolePermissions(roleId: string | number): Promise<StrapiPermission[]> {
    try {
      const resp = await adminFetch<any>(`/admin/roles/${roleId}/permissions`);
      const perms = resp.data || resp || [];
      return Array.isArray(perms) ? perms : [];
    } catch {
      return [];
    }
  },

  async getById(id: string | number) {
    const response = await adminFetch<any>(`/admin/roles/${id}`);
    return response.data || response;
  },

  /**
   * Create role then assign permissions (with fields) via separate PUT.
   */
  async create(roleData: any) {
    const { isActive, isSystem, permissions: rawPerms, ...rest } = roleData;
    const permsToSave: StrapiPermission[] = rawPerms || [];

    const response = await adminFetch<any>('/admin/roles', {
      method: 'POST',
      body: JSON.stringify({
        name: rest.name,
        description: rest.description || '',
      }),
    });

    const createdRole = response.data;

    if (createdRole?.id && permsToSave.length > 0) {
      const payload = permsToSave.map(p => ({
        action: p.action,
        ...(p.subject ? { subject: p.subject } : {}),
        ...(p.properties?.fields?.length ? { properties: { fields: p.properties.fields } } : {}),
      }));
      await adminFetch<any>(`/admin/roles/${createdRole.id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: payload }),
      });
    }

    return {
      ...createdRole,
      id: createdRole?.id?.toString(),
      isSystem: false,
      isActive: true,
      permissions: permsToSave,
    };
  },

  /**
   * Update role info then reassign permissions (with fields) via PUT.
   */
  async update(id: string | number, roleData: any) {
    const { isActive, isSystem, permissions: rawPerms, ...rest } = roleData;
    const permsToSave: StrapiPermission[] = rawPerms || [];

    const response = await adminFetch<any>(`/admin/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: rest.name,
        description: rest.description || '',
      }),
    });

    const payload = permsToSave.map(p => ({
      action: p.action,
      ...(p.subject ? { subject: p.subject } : {}),
      ...(p.properties?.fields?.length ? { properties: { fields: p.properties.fields } } : {}),
    }));
    await adminFetch<any>(`/admin/roles/${id}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions: payload }),
    });

    const updatedRole = response.data;
    return {
      ...updatedRole,
      id: updatedRole?.id?.toString(),
      permissions: permsToSave,
    };
  },

  async delete(id: string | number) {
    const response = await adminFetch<any>(`/admin/roles/${id}`, {
      method: 'DELETE',
    });
    return response.data;
  },
};
